import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { gridSizeMdLg12, gridSizeMdLg6 } from '@/shared';

import {
  CustomAutocomplete,
  CustomAutocompleteArrString,
  CustomTextArea,
  CustomTextField,
  SampleRadioButtonsGroup,
  SingleFormBoxScene,
} from '@/shared/components/ui';
import { useCreateTicketSoporteCliente, useUpdateTicketSoporteCliente } from '@/shared/actions/erp/tickets-soporte-cliente';
import { Button, Grid, Typography } from '@mui/material';
import { ESTADO_SOLUCION, ESTADO_TICKET, ITicketSoporteCliente } from '@/shared/interfaces/erp/tickets-soporte-clientes';
import { useFetchCategoriaIncidentes } from '@/shared/actions/erp/categoria-incidentes';
import { useFetchSubcategoriaIncidencia } from '@/shared/actions/erp/subcategoria-incidentes';
import { useFetchCausalIncidencia } from '@/shared/actions/erp/causal-incidentes';
import { useFetchDepartamentos } from '@/shared/actions/erp';
import { Contrato } from '@/shared/interfaces';
import HistorialInformeSolucion from './HistorialInformeSolucion';
import { useAuthStore } from '@/store/auth';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';


const returnUrlNuevoTicket = '/tickets-soporte-cliente/nuevo-ticket';

export interface SaveNuevoTicketInterface {
  titlePage?: string;
  editTicketSoporte?: ITicketSoporteCliente;
  contrato?: Contrato
}

type SaveFormData = ITicketSoporteCliente & {
  identificacion ?: string;
  razon_social ?: string;
  servicio ?: string;
  tipo_plan ?: string;
  plan_contratado ?: string;
  estado_contrato ?: string;
  estado_servicio ?: string;
  causal_incidencia ?: string;
  fecha_creacion ?: string;
  departamento ?: string;
  categoria_incidencia ?: string;
  subcategoria_incidencia ?: string;
  obser ?: string;
  informe_solucion ?: any;
  solucionado?:string;
  descripcion_informacion_solucion: string;
  datos_informacion_solucion?: any;
};

const SaveNuevoTicket: React.FC<SaveNuevoTicketInterface> = ({
  titlePage,
  editTicketSoporte,
  contrato
}) => {
  const navigate = useNavigate();
  const [shrink, setShrink] = useState(false);
  const auth = useAuthStore().user;
  

  ///* form
  const form = useForm<SaveFormData>({
    // resolver: yupResolver(newCategoryProductSchema),
    defaultValues: {
      fecha_creacion: dayjs().format('YYYY-MM-DD HH:mm:ss'),
      estado: false,
    },
  });
  const {
    handleSubmit,
    reset,
    formState: { errors },
  } = form;


  ///* mutations
  const createTicketSoporteCliente = useCreateTicketSoporteCliente({
    navigate,
    returnUrl:returnUrlNuevoTicket
  });
  const updateTicketSoporteCliente = useUpdateTicketSoporteCliente({
    navigate,
    returnUrl:returnUrlNuevoTicket
  });
  const {data:dataCategoriasIncidentes, isLoading:isLoadingCategoriasIncidentes} = useFetchCategoriaIncidentes({
    page_size: 600
  });
  const {data:dataSubcategoriaIncidencia, isLoading:isLoadingSubcategoriaIncidencia} = useFetchSubcategoriaIncidencia({
    page_size: 600,
    categoria_incidencia:form.watch().categoria,
    enabled: !!form.watch().categoria
  });
  const {data:dataCausalIncidencia, isLoading:isLoadingCausalIncidencia} = useFetchCausalIncidencia({
    page_size: 600,
    categoria_incidencia:form.watch().categoria,
    subcategoria_incidencia:form.watch().subcategoria,
    enabled: !!form.watch().categoria && !!form.watch().subcategoria
  });
  const{data:dataDepartamentos, isLoading:isLoadingDepartamento} = useFetchDepartamentos({page_size:600})
  const departamentoUser = dataDepartamentos?.data?.data?.find((item)=>item.codigo==auth?.departamento)



  ///* handlers
  const handleSubmitInformacionSolucion = ()=>{
    const datosEscritos = {
      fecha: dayjs().format('YYYY-MM-DD HH:mm:ss'),
      usuario: auth?.username,
      razon_social: auth?.razon_social,
      descripcion: form.getValues()?.descripcion_informacion_solucion,
      departamento: form.getValues()?.departamento || departamentoUser?.nombre,
    }
    form.reset({
      ...form.getValues(),
      informacion_solucion: form.getValues()?.informacion_solucion 
      && form.getValues()?.informacion_solucion.length > 0?
      [
        ...form.getValues()?.informacion_solucion,
        datosEscritos
      ]              
      :
      [
        datosEscritos
      ],
      descripcion_informacion_solucion: ""
    })
  }

  const onSave = () => {
    if(form.getValues().descripcion_informacion_solucion){
      toast.warning('Envíe el mensaje de informe de solución para guardar la información.')
      return
    }
    const data : SaveFormData = {
      estado:true,
      canal_comunicacion:form.getValues().canal_comunicacion,
      observaciones:form.getValues().observaciones,
      nombre_contacto:form.getValues().nombre_contacto,
      telefono_contacto:form.getValues().telefono_contacto,
      parentesco_contacto:form.getValues().parentesco_contacto,
      informacion_solucion:form.getValues().informacion_solucion,
      cliente:form.getValues().cliente,
      contrato:form.getValues().contrato,
      categoria:form.getValues().categoria!,
      subcategoria:form.getValues().subcategoria!,
      causal:form.getValues().causal!,
      descripcion_informacion_solucion:"",
      departamento_responsable: form.getValues().departamento || departamentoUser?.codigo,
    }
    
    if(!editTicketSoporte){
      // alert("saved")
      data.fecha_apertura=dayjs().toISOString()
      data.estado_solucion=form.getValues().solucionado=="SI"?ESTADO_SOLUCION.FINALIZADO:ESTADO_SOLUCION.PENDIENTE
      data.estado_ticket=form.getValues().solucionado=="SI"?ESTADO_TICKET.RESUELTO:ESTADO_TICKET.ABIERTO
      data.ticket_cerrado=form.getValues().solucionado=="SI"?true:false
      createTicketSoporteCliente.mutateAsync(data)
    }else{
      data.id_ticket_soporte=form.getValues().id_ticket_soporte
      data.estado_solucion=form.getValues().solucionado=="SI"?ESTADO_SOLUCION.FINALIZADO:ESTADO_SOLUCION.PENDIENTE
      data.estado_ticket=form.getValues().solucionado=="SI"?ESTADO_TICKET.RESUELTO:ESTADO_TICKET.ABIERTO
      data.ticket_cerrado=form.getValues().solucionado=="SI"?true:false
      if(form.getValues().solucionado=="SI"){
        data.fecha_fin=dayjs().toISOString()
      }
      updateTicketSoporteCliente.mutateAsync({id:data.id_ticket_soporte!, data})
    }
    
  };

  ///* effects
  useEffect(() => {
    if (editTicketSoporte) {
      setShrink(true);
      reset(editTicketSoporte as any);
    }
    if(contrato){
      reset({
        ...editTicketSoporte,
        identificacion:contrato?.cliente_data?.identificacion,
        razon_social:contrato?.cliente_data?.razon_social,
        servicio:contrato?.tipo_servicio,
        tipo_plan:contrato?.plan_data?.tipo_plan,
        plan_contratado:contrato?.plan_data?.nombre,
        estado_contrato:contrato?.estado_contrato,
        estado_servicio:contrato?.estado_contrato || contrato?.estado_contrato,
        cliente:contrato?.cliente_data?.codigo,
        contrato:contrato?.codigo,
        departamento:editTicketSoporte?.departamento_responsable,
      })
    }
  }, [editTicketSoporte, contrato]);

  return (
    <SingleFormBoxScene
      titlePage={titlePage}
      onCancel={() => {
        navigate(returnUrlNuevoTicket);
      }}
      cancelTextBtn='Cancelar'
      saveTextBtn={form.watch().solucionado=="SI"?"Cerrar Ticket":'Registrar'}
      onSave={handleSubmit(onSave)}
      formGridSize={gridSizeMdLg12}
    >
      <Grid container item {...gridSizeMdLg12} spacing={2} direction={"row-reverse"}>
        <CustomTextField
          name="fecha_apertura"
          label="Fecha"
          control={form.control}
          defaultValue={dayjs(form.getValues().fecha_apertura).format('YYYY-MM-DD HH:mm:ss')}
          error={errors.fecha_apertura}
          helperText={errors.fecha_apertura?.message}
          shrink={shrink}
          disabled
        />
        {/* <Grid item {...gridSizeMdLg6} spacing={2}>
          <Grid item textAlign={"right"}>
            <Typography variant="body1">Estado Cliente</Typography>
            <Typography variant="h3">ACTIVO</Typography>
          </Grid>

        </Grid> */}
      </Grid>
      <Grid container item {...gridSizeMdLg12} spacing={2}>
        
          <CustomTextField
            name="identificacion"
            label="Identificación"
            control={form.control}
            defaultValue={contrato?.cliente_data?.identificacion || ""}
            error={errors.identificacion}
            helperText={errors.identificacion?.message}
            shrink={shrink}
            size={gridSizeMdLg6}
            disabled
          />
          <CustomTextField
            name="razon_social"
            label="Razón Social"
            control={form.control}
            defaultValue={contrato?.cliente_data?.razon_social || ""}
            error={errors.razon_social}
            helperText={errors.razon_social?.message}
            shrink={shrink}
            size={gridSizeMdLg6}
            disabled
          />
          <CustomTextField
            name="servicio"
            label="Servicio"
            control={form.control}
            defaultValue={contrato?.tipo_servicio || ""}
            error={errors.servicio}
            helperText={errors.servicio?.message}
            shrink={shrink}
            size={gridSizeMdLg6}
            disabled
          />
          <CustomTextField
            name="tipo_plan"
            label="Tipo Plan"
            control={form.control}
            defaultValue={contrato?.plan_data?.tipo_plan || ""}
            error={errors.tipo_plan}
            helperText={errors.tipo_plan?.message}
            shrink={shrink}
            size={gridSizeMdLg6}
            disabled
          />
          <CustomTextField
            name="plan_contratado"
            label="Plan Contratado"
            control={form.control}
            defaultValue={contrato?.plan_data?.nombre || ""}
            error={errors.plan_contratado}
            helperText={errors.plan_contratado?.message}
            shrink={shrink}
            size={gridSizeMdLg6}
            disabled
          />
          <CustomTextField
            name="estado_servicio"
            label="Estado de Servicio"
            control={form.control}
            defaultValue={contrato?.estado_contrato || ""}
            error={errors.estado_servicio}
            helperText={errors.estado_servicio?.message}
            shrink={shrink}
            size={gridSizeMdLg6}
            disabled
          />
        
        <Grid container item {...gridSizeMdLg12} spacing={2}>
          <Grid item {...gridSizeMdLg12}>
            <Typography variant='h6'>Datos de la Incidencia</Typography>
          </Grid>
          <CustomAutocomplete
            name="departamento"
            label="Departamento"
            options={dataDepartamentos?.data.data || []}
            control={form.control}
            defaultValue={undefined}
            valueKey={'nombre'}
            actualValueKey='codigo'
            error={errors.departamento}
            helperText={errors.departamento?.message}
            isLoadingData={isLoadingDepartamento}
            size={gridSizeMdLg6}
          />
          <CustomAutocomplete
            label="Categoría"
            name="categoria"
            control={form.control}
            defaultValue={form.getValues().categoria}
            error={errors.categoria}
            helperText={errors.categoria?.message}
            options={dataCategoriasIncidentes?.data.data || []}
            isLoadingData={isLoadingCategoriasIncidentes}
            valueKey="nombre"
            actualValueKey='codigo'
            optionLabelForEdit={form.getValues().categoria}
            onChangeValue={(_value)=>form.reset({
              ...form.getValues(),
              subcategoria:undefined,
              causal:undefined
            })}
            size={gridSizeMdLg6}
          />
          <CustomAutocomplete
            label="Subcategoría"
            name="subcategoria"
            control={form.control}
            defaultValue={form.getValues().subcategoria}
            error={errors.subcategoria}
            helperText={errors.subcategoria?.message}
            options={dataSubcategoriaIncidencia?.data.data || []}
            isLoadingData={isLoadingSubcategoriaIncidencia}
            valueKey="nombre"
            actualValueKey='codigo'
            optionLabelForEdit={form.getValues().subcategoria}
            onChangeValue={(_value)=>form.reset({
              ...form.getValues(),
              causal:undefined
            })}
            size={gridSizeMdLg6}
          />
          <CustomAutocomplete
            label="Causal Incidencia"
            name="causal"
            control={form.control}
            defaultValue={form.getValues().causal}
            error={errors.causal}
            helperText={errors.causal?.message}
            options={dataCausalIncidencia?.data.data || []}
            isLoadingData={isLoadingCausalIncidencia}
            valueKey="nombre"
            actualValueKey='codigo'
            // optionLabelForEdit={form.getValues().causal_incidencia}
            // onChangeValue={(e) => console.log(e)}
            size={gridSizeMdLg6}
          />
        </Grid>
        <Grid container item {...gridSizeMdLg12} spacing={2}>
          {/* <Grid item {...gridSizeMdLg12}>
            <Typography variant='h6'>Tiempo Aproximado de Gestión</Typography>
          </Grid> */}
          <CustomAutocompleteArrString
            name="canal_comunicacion"
            label="Canal de Comunicación"
            options={[
              "Facebook",
              "Whatsapp",
              "Central IP",
              "PBX Claro",
              "Página Web",
              "Presencial",
            ]}
            isLoadingData={false}
            control={form.control}
            defaultValue={form.getValues().canal_comunicacion}
            error={errors.canal_comunicacion}
            helperText={errors.canal_comunicacion?.message}
            size={gridSizeMdLg12}
          />


          {/* <SampleRadioButtonsGroup
            label={"Canal de Comunicación"}
            name={"canal_comunicacion"}
            control={form.control}
            defaultValue={""}
            size={gridSizeMdLg12}
            options={[
              {
                value: "Facebook",
                label: "Facebook",
              },
              {
                value: "Whatsapp",
                label: "Whatsapp",
              },
              {
                value: "Central IP",
                label: "Central IP",
              },
              {
                value: "PBX Claro",
                label: "PBX Claro",
              },
              {
                value: "Página Web",
                label: "Página Web",
              },
              {
                value: "Presencial",
                label: "Presencial",
              },
            ]}
          /> */}

          <CustomTextArea
            name="observaciones"
            label="Observación Incidencia"
            control={form.control}
            defaultValue={form.getValues().observaciones || ""}
            error={errors.observaciones as any}
            helperText={errors.observaciones?.message as any}
            shrink={shrink}
          />
        </Grid>
        <Grid container item {...gridSizeMdLg12} spacing={2}>
          <Grid item {...gridSizeMdLg12}>
            <Typography variant='h6'>Datos para Contacto</Typography>
          </Grid>
          <CustomTextField
            name="nombre_contacto"
            label="Nombre Contacto"
            control={form.control}
            defaultValue={form.getValues().nombre_contacto || ""}
            error={errors.nombre_contacto}
            helperText={errors.nombre_contacto?.message}
            shrink={shrink}
          />
          <CustomTextField
            name="telefono_contacto"
            label="Teléfono Contacto"
            control={form.control}
            defaultValue={form.getValues().telefono_contacto || ""}
            error={errors.telefono_contacto}
            helperText={errors.telefono_contacto?.message}
            shrink={shrink}
          />
          <CustomTextField
            name="parentesco_contacto"
            label="Parentesco Contacto"
            control={form.control}
            defaultValue={form.getValues().parentesco_contacto || ""}
            error={errors.parentesco_contacto}
            helperText={errors.parentesco_contacto?.message}
            shrink={shrink}
          />

        </Grid>
        <Grid container item {...gridSizeMdLg12} spacing={2}>
          <Grid item {...gridSizeMdLg12}>
            <Typography variant='h6'>Estado de Ticket</Typography>
          </Grid>
          {editTicketSoporte && (
            <SampleRadioButtonsGroup
              label={"Solucionado"}
              name={"solucionado"}
              control={form.control}
              defaultValue={"NO"}
              size={gridSizeMdLg12}
              options={[
                {
                  value: "SI",
                  label: "SI",
                },
                {
                  value: "NO",
                  label: "NO",
                },
              ]}
            />
          )}
          <HistorialInformeSolucion informe_solucion={form.getValues()?.informacion_solucion}/>
          <CustomTextArea
            name="descripcion_informacion_solucion"
            label="Informe de Solución"
            control={form.control}
            defaultValue={form.getValues().descripcion_informacion_solucion || ""}
            error={errors.descripcion_informacion_solucion}
            helperText={errors.descripcion_informacion_solucion?.message}
            shrink={shrink}
          />
          <Grid item {...gridSizeMdLg12}>
            <Button fullWidth variant="contained" 
            disabled={ 
              !form.getValues()?.descripcion_informacion_solucion 
              || form.getValues()?.descripcion_informacion_solucion?.length < 5} 
            onClick={handleSubmitInformacionSolucion}>Enviar</Button>
          </Grid>
          {/* <Grid item>
            <Button
              variant="contained"
              onClick={()=>setOpenInfoCliente(true)}>
              VER MAS INFORMACIÓN DEL CLIENTE
            </Button>
          </Grid> */}
        </Grid>
      </Grid>


      {/* 
      // =======================================================================
      // Modal de información de cliente
      // ======================================================================= */}
      {/* <ModalInfoCliente
        open={openInfoCliente}
        onClose={()=>{
          setOpenInfoCliente(false)
        }}
        form={form}
        onConfirm={()=>{
          setOpenInfoCliente(false)
        }}
      /> */}
      
    </SingleFormBoxScene>
  );
};

export default SaveNuevoTicket;
